'use client';

import dynamic from 'next/dynamic';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import TrailPageType from '@alltrails/amplitude/enums/TrailPageType';
import logTrailDetailsConditionsCTATapped from '@alltrails/amplitude/events/logTrailDetailsConditionsCTATapped';
import logTrailDetailsConditionsViewed from '@alltrails/amplitude/events/logTrailDetailsConditionsViewed';
import logTrailDetailsDateSelected from '@alltrails/amplitude/events/logTrailDetailsDateSelected';
import ConditionsCard from '@alltrails/conditions/components/ConditionsCard/ConditionsCard';
import ConditionsErrorCard from '@alltrails/conditions/components/ConditionsErrorCard/ConditionsErrorCard';
import ConditionsUpsellCard, { ConditionsUpsellCardLoading } from '@alltrails/conditions/components/ConditionsUpsellCard/ConditionsUpsellCard';
import DesktopDailySummaries, { DesktopDailySummariesLoading } from '@alltrails/conditions/components/DesktopDailySummaries/DesktopDailySummaries';
import { trailConditionsIntervalMs } from '@alltrails/conditions/components/MapConditionsSidebar/constants';
import MobileDailySummaries, { MobileDailySummariesLoading } from '@alltrails/conditions/components/MobileDailySummaries/MobileDailySummaries';
import { useGetTrailWeatherConditionsQuery, useGetTrailWeatherOverviewQuery } from '@alltrails/conditions/redux/conditionsApi';
import { updateSelectedUTC } from '@alltrails/conditions/redux/conditionsSlice';
import { Day, SelectedDay } from '@alltrails/conditions/types/DailySummaryDay';
import { getLocalizedDateStart, getLocalizedDateString, getLocalizedUTCDate, getUTCFromLocalized } from '@alltrails/conditions/utils/timeHelpers';
import { CONDITIONS_FEATURE_ENTITLEMENTS, hasEveryFeatureEntitlement } from '@alltrails/context/hooks/useFeatureEntitlements';
import useUser from '@alltrails/context/hooks/useUser';
import useIsVisible from '@alltrails/core/hooks/useIsVisible';
import { millisecondsInDay } from '@alltrails/data-formatting/dateConstants';
import Heading from '@alltrails/denali/components/Heading';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import { updateDynamicStyleIds } from '@alltrails/maps/redux/reducer';
import type { ConditionStyleId } from '@alltrails/maps/types/Styles';
import {
  getTrailConditionsAlertDisplayed,
  getTrailConditionsScreen,
  getTrailDetailsDaysInAdvance,
  getTrailDetailsPageTerrainCondition
} from '@/utils/conditionsAmplitudeHelpers';
import { useAppDispatch, useAppSelector } from '@/utils/redux';
import { setIsTrailMapOpen } from '@/utils/trailPageSlice';
import CardModule from '../CardModule/CardModule';
import Divider from '../layouts/Divider/Divider';
import ResponsiveGrid from '../layouts/ResponsiveGrid/ResponsiveGrid';
import SurfaceType from '../SurfaceType/SurfaceType';
import TrailConditionsInstallInterstitial from '../TrailConditionsInstallInterstitial/TrailConditionsInstallInterstitial';
import styles from './TrailConditions.module.scss';

const ConditionsUpsellModal = dynamic(() => import('@alltrails/conditions/components/ConditionsUpsellModal'), { ssr: false });
const SignUpModal = dynamic(() => import('@alltrails/user-auth/components/SignUpModal'), { ssr: false });

type TrailConditionsProps = {
  showSurfaceType?: boolean;
  trailId: number;
  trailPageType: TrailPageType;
};

const TrailConditions = ({ showSurfaceType, trailId, trailPageType }: TrailConditionsProps) => {
  const [surfaceTypeVisible, setSurfaceTypeVisible] = useState(!!showSurfaceType);
  const isMobile = useIsMobileSizedScreen();
  const dispatch = useAppDispatch();
  const user = useUser();
  const showConditionsCard = hasEveryFeatureEntitlement(user, CONDITIONS_FEATURE_ENTITLEMENTS);
  const { displayMetric, selectedUTC } = useAppSelector(state => ({
    displayMetric: state.context.displayMetric,
    selectedUTC: state.conditions.selectedUTC
  }));

  useEffect(() => {
    const initialUTC = Math.floor(Date.now() / trailConditionsIntervalMs) * trailConditionsIntervalMs;
    dispatch(updateSelectedUTC(initialUTC));
  }, [dispatch]);

  const [utcOffsetSeconds, setUTCOffsetSeconds] = useState<number>();

  const localizedDateString = useMemo(
    () => (selectedUTC && utcOffsetSeconds !== undefined ? getLocalizedDateString(selectedUTC, utcOffsetSeconds) : undefined),
    [selectedUTC, utcOffsetSeconds]
  );

  const { data, isError, isFetching, isLoading, refetch } = useGetTrailWeatherOverviewQuery({
    date: localizedDateString,
    includeGradient: true,
    includePrecipitation: true,
    sentenceUnits: displayMetric ? 'm' : 'i',
    trailId
  });

  useEffect(() => {
    setUTCOffsetSeconds(data?.secondsFromUtc);
  }, [data?.secondsFromUtc]);

  const utcTodayStart = useMemo(
    () => (utcOffsetSeconds !== undefined ? getLocalizedDateStart(Date.now(), utcOffsetSeconds) : undefined),
    [utcOffsetSeconds]
  );

  const selectedDayStart = useMemo(
    () => (selectedUTC && utcOffsetSeconds !== undefined ? getLocalizedDateStart(selectedUTC, utcOffsetSeconds) : undefined),
    [selectedUTC, utcOffsetSeconds]
  );

  const { alert, overview } = useMemo(() => {
    if (!data || isFetching) {
      // When isFetching is true, a different day has been selected, but the alert/overview could still exist from a prior call, so ignore the data until isFetching is false
      return { alert: undefined, overview: undefined };
    }
    return data;
  }, [data, isFetching]);

  const {
    data: conditionsData,
    isError: conditionsIsError,
    isFetching: conditionsIsFetching,
    isLoading: conditionsIsLoading,
    isUninitialized: conditionsIsUninitialized,
    refetch: conditionsRefetch
  } = useGetTrailWeatherConditionsQuery(
    { date: localizedDateString || '', sentenceUnits: displayMetric ? 'm' : 'i', trailId },
    { skip: !localizedDateString || !showConditionsCard }
  );

  const hasLoggedViewEvent = useRef(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const isVisible = useIsVisible(contentRef);

  useEffect(() => {
    if (isVisible && !hasLoggedViewEvent.current) {
      if (showConditionsCard) {
        if (conditionsData) {
          const { conditionKey, relatedReviewCount, subtitleSource } = conditionsData.terrain;
          logTrailDetailsConditionsViewed({
            alert_displayed: getTrailConditionsAlertDisplayed(alert?.alertType),
            has_alert: Boolean(alert),
            num_terrain_reviews_shown: relatedReviewCount || 0,
            terrain_subtitle_shown: subtitleSource === 'reviews' ? 'related reviews' : 'ugc',
            terrain_type_shown: getTrailDetailsPageTerrainCondition(conditionKey),
            trail_id: trailId
          });
          hasLoggedViewEvent.current = true;
        }
      } else {
        logTrailDetailsConditionsViewed({
          alert_displayed: getTrailConditionsAlertDisplayed(alert?.alertType),
          has_alert: Boolean(alert),
          num_terrain_reviews_shown: 0,
          trail_id: trailId
        });
        hasLoggedViewEvent.current = true;
      }
    }
  }, [alert, conditionsData, isVisible, showConditionsCard, trailId]);

  const days = useMemo(() => {
    if (data?.dailySummaries && utcTodayStart) {
      return data.dailySummaries.map<Day | SelectedDay>(({ gradient, highTemperature, iconCode, lowTemperature }, index) => {
        // The start of each day, localized to the trail's timezone
        const utcMs = utcTodayStart + millisecondsInDay * index;
        const isSelected = utcMs === selectedDayStart;

        if (isSelected && overview && utcOffsetSeconds !== undefined) {
          return {
            alert,
            gradient,
            precipitationChance: overview.chanceOfPrecipitation,
            summary: overview.summary,
            sunrise: overview.sunriseTime ? getLocalizedUTCDate(overview.sunriseTime, utcOffsetSeconds) : undefined,
            sunset: overview.sunsetTime ? getLocalizedUTCDate(overview.sunsetTime, utcOffsetSeconds) : undefined,
            tempCurrent: overview.currentTemperature,
            tempHigh: overview.highTemperature,
            tempLow: overview.lowTemperature,
            utcMs,
            weatherIconCode: iconCode // Use the icon code from the daily summary rather than the overview to avoid the icon changing when the selected day changes
          };
        }

        return { gradient, tempHigh: highTemperature, tempLow: lowTemperature, utcMs, weatherIconCode: iconCode };
      });
    }
    return undefined;
  }, [alert, data?.dailySummaries, overview, selectedDayStart, utcOffsetSeconds, utcTodayStart]);

  const onChange = useCallback(
    (localizedUTC: number, index: number) => {
      if (utcOffsetSeconds !== undefined) {
        const newSelectedUTC = getUTCFromLocalized(localizedUTC, utcOffsetSeconds);
        dispatch(updateSelectedUTC(newSelectedUTC));
        logTrailDetailsDateSelected({ days_in_advance: getTrailDetailsDaysInAdvance(index), trail_id: trailId });
      }
    },
    [dispatch, trailId, utcOffsetSeconds]
  );

  const renderDaySelectComponent = () => {
    if (isError) {
      return (
        <ConditionsErrorCard className={styles.overviewErrorCard} onRetry={refetch} showIllustration={isMobile ? false : true} trailId={trailId} />
      );
    }

    if (isMobile) {
      if (isLoading || !days || !selectedDayStart) {
        return <MobileDailySummariesLoading />;
      }
      return <MobileDailySummaries days={days} onChange={onChange} selectedUTCMs={selectedDayStart} />;
    }

    if (isLoading || !days || !selectedDayStart) {
      return <DesktopDailySummariesLoading />;
    }
    return <DesktopDailySummaries days={days} onChange={onChange} selectedUTCMs={selectedDayStart} useFullWeekdayNames={!surfaceTypeVisible} />;
  };

  const [initialTheme, setInitialTheme] = useState(overview?.backgroundTheme);
  const [showConditionsUpsellModal, setShowConditionsUpsellModal] = useState(false);
  const [showSignupModal, setShowSignupModal] = useState(false);
  const [showInstallInterstitial, setShowInstallInterstitial] = useState(false);

  useEffect(() => {
    if (!initialTheme && overview?.backgroundTheme) {
      setInitialTheme(overview?.backgroundTheme);
    }
  }, [initialTheme, overview?.backgroundTheme]);

  const openTrailMap = useCallback(() => {
    dispatch(setIsTrailMapOpen(true));
  }, [dispatch]);

  const onConditionsCardCTAClick = useCallback(
    (ctaType: 'ground conditions' | 'weather map', condition: ConditionStyleId) => {
      logTrailDetailsConditionsCTATapped({ action_taken: 'cta tapped', cta_type: ctaType, trail_id: trailId });

      if (isMobile) {
        setShowInstallInterstitial(true);
      } else {
        openTrailMap();
        dispatch(updateDynamicStyleIds([condition]));
      }
    },
    [dispatch, isMobile, openTrailMap, trailId]
  );

  const renderConditionsCard = () => {
    if (isError) {
      return null;
    }
    if (showConditionsCard) {
      return (
        <ConditionsCard
          data={conditionsData}
          isError={conditionsIsError}
          isFetching={conditionsIsFetching || conditionsIsLoading || conditionsIsUninitialized}
          onRetry={conditionsRefetch}
          onTerrainClick={condition => onConditionsCardCTAClick('ground conditions', condition)}
          onWeatherMapClick={condition => onConditionsCardCTAClick('weather map', condition)}
          theme={overview?.backgroundTheme}
          trailId={trailId}
        />
      );
    }
    if (!initialTheme) {
      return <ConditionsUpsellCardLoading />;
    }
    return (
      <ConditionsUpsellCard
        initialTheme={initialTheme}
        onShowUpsellModal={() => {
          if (!user) {
            setShowSignupModal(true);
          } else {
            setShowConditionsUpsellModal(true);
          }
        }}
        onWeatherMapClick={(condition, index) =>
          logTrailDetailsConditionsCTATapped({
            action_taken: 'cta tapped',
            conditions_screen: getTrailConditionsScreen(condition),
            cta_index: index,
            cta_type: 'weather map',
            trail_id: trailId
          })
        }
      />
    );
  };

  const conditionsCard = renderConditionsCard();

  return (
    <>
      <div className={styles.container}>
        <Heading as="h4" size="400">
          {isMobile ? <FormattedMessage defaultMessage="Weather" /> : <FormattedMessage defaultMessage="Conditions" />}
        </Heading>
        <ResponsiveGrid equalColumns tabletLastChildFull>
          <CardModule>
            <div className={styles.summaryWrapper} ref={contentRef}>
              {renderDaySelectComponent()}
            </div>
          </CardModule>
          {conditionsCard !== null && <CardModule>{conditionsCard}</CardModule>}
          {!isMobile && surfaceTypeVisible && (
            <CardModule>
              <SurfaceType onHide={() => setSurfaceTypeVisible(false)} trailId={trailId} trailPageType={trailPageType} />
            </CardModule>
          )}
        </ResponsiveGrid>
        {isMobile && surfaceTypeVisible && (
          <div className={styles.mobileSurfaceType}>
            <Divider />
            <SurfaceType onHide={() => setSurfaceTypeVisible(false)} trailId={trailId} trailPageType={trailPageType} />
          </div>
        )}
      </div>
      <TrailConditionsInstallInterstitial
        isOpen={showInstallInterstitial}
        onRequestClose={() => setShowInstallInterstitial(false)}
        trailId={trailId}
      />
      <ConditionsUpsellModal
        isOpen={showConditionsUpsellModal}
        onCloseRequest={() => {
          setShowConditionsUpsellModal(false);
        }}
        upsellTrigger="advanced conditions layer tapped"
      />
      <SignUpModal
        closeModal={() => setShowSignupModal(false)}
        deeplinkData={{ pageId: trailId, pageType: 'trail' }}
        isOpen={showSignupModal}
        onSuccess={signedInUser => {
          setShowSignupModal(false);

          if (!hasEveryFeatureEntitlement(signedInUser, CONDITIONS_FEATURE_ENTITLEMENTS)) {
            setShowConditionsUpsellModal(true);
          }
        }}
        trigger="advanced conditions trail page card tapped"
        upsellTrigger="advanced conditions layer tapped"
      />
    </>
  );
};

export default TrailConditions;
